import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { AlertTriangle, Search, Boxes } from "lucide-react";

const formatMXN = (n) =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [onlyLow, setOnlyLow] = useState(false);

  const load = async () => {
    const { data } = await api.get("/products", {
      params: { q: q || undefined, low_stock: onlyLow },
    });
    setItems(data);
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [q, onlyLow]); // eslint-disable-line

  const totalValue = items.reduce((s, p) => s + (p.cost || p.price) * p.stock, 0);

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="inventory-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-clay font-semibold">Almacén</div>
          <h1 className="font-outfit text-4xl font-bold text-stone">Inventario</h1>
          <p className="text-stone/60 mt-1">Niveles de stock y valor del inventario.</p>
        </div>
        <div className="bg-white border border-warm-border rounded-xl px-5 py-3 text-right">
          <div className="text-[11px] uppercase tracking-[0.2em] text-stone/50 font-semibold">
            Valor inventario
          </div>
          <div className="font-outfit text-2xl font-bold text-stone" data-testid="inventory-total-value">
            {formatMXN(totalValue)}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-warm-border overflow-hidden shadow-sm">
        <div className="p-4 flex items-center gap-3 border-b border-warm-border">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone/40" />
            <Input
              placeholder="Buscar producto..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="inventory-search"
              className="pl-10 border-warm-border focus-visible:ring-clay focus-visible:ring-offset-0"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-stone cursor-pointer select-none">
            <input
              type="checkbox"
              checked={onlyLow}
              onChange={(e) => setOnlyLow(e.target.checked)}
              data-testid="inventory-low-stock-toggle"
              className="w-4 h-4 accent-clay"
            />
            Solo stock bajo
          </label>
        </div>

        <table className="w-full">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-stone/50">
              <th className="px-6 py-3 font-semibold">Producto</th>
              <th className="px-6 py-3 font-semibold">Código</th>
              <th className="px-6 py-3 font-semibold text-right">Costo</th>
              <th className="px-6 py-3 font-semibold text-right">Precio</th>
              <th className="px-6 py-3 font-semibold text-right">Stock</th>
              <th className="px-6 py-3 font-semibold text-right">Valor</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => {
              const low = p.stock <= p.min_stock;
              return (
                <tr key={p.id} className={`border-t border-warm-border ${low ? "bg-amber/5" : "hover:bg-sand/60"} transition-colors`}>
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2">
                      {low && <AlertTriangle className="w-4 h-4 text-amber" />}
                      <div>
                        <div className="font-medium text-stone">{p.name}</div>
                        {p.sku && <div className="text-xs text-stone/50 font-mono">{p.sku}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-3 text-sm font-mono text-stone/70">{p.barcode}</td>
                  <td className="px-6 py-3 text-right text-sm text-stone/70">{formatMXN(p.cost)}</td>
                  <td className="px-6 py-3 text-right font-medium text-stone">{formatMXN(p.price)}</td>
                  <td className="px-6 py-3 text-right">
                    <span className={`inline-block px-2.5 py-0.5 rounded-md text-sm font-medium ${
                      low ? "bg-amber/15 text-[#B97A30]" : "bg-sage/15 text-[#5C7363]"
                    }`}>
                      {p.stock} / min {p.min_stock}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-right font-outfit font-semibold text-stone">
                    {formatMXN((p.cost || p.price) * p.stock)}
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-stone/50">
                  <Boxes className="w-10 h-10 mx-auto mb-2 text-stone/20" />
                  Sin productos para mostrar.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
